import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect } from 'react';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../../../utilities/analytics';
import { KmxContainer } from '../../../../components/KmxContainer';
import { TextReveal } from '../../../../components/TextReveal';
import useAppState from '../../../../common/state/appState';
import { useMuiMediaQuery } from '../../../../utilities/hooks';

export const FirstTimeHero = () => {
    const serveUFTContent = useAppState((state) => state.serveUFTContent);
    const { isMobile } = useMuiMediaQuery();
    /**
     * @description: return content based on toggle
     * @param uftContent: content we return if serveUFTContent is enabled
     * @param controlContent: content we return if serveUFTContent is not enabled
     * @returns any
     */
    const returnContent = (uftContent: any, controlContent: any) => (serveUFTContent ? uftContent : controlContent);
    const handleOnClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();

        trackEDDL({
            event: 'searchInitiated',
            linkDetails: {
                name: returnContent('Find your car', 'Search cars'),
                position: 'Homepage',
            },
            search: {
                initiationMethod: 'Homepage|search cars|Homepage',
            },
        });

        setTimeout(() => {
            window.location.href = '/cars';
        }, ANALYTICS_CONSTANTS.TRACKING_DELAY);
    };

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            trackEDDL({
                event: ANALYTICS_CONSTANTS.EVENTS.PAGE.STATE,
                pageState: { pageState: 'hp-HeroBrandMessage' },
            });
        }, ANALYTICS_CONSTANTS.PAGE_STATE_LOAD_TIME);

        return () => clearTimeout(timeoutId);
    }, []);

    return (
        <KmxContainer
            maxWidth="xl"
            sx={{
                px: returnContent(undefined, { xs: 2, sm: 2 }),
                py: { xs: 1.5, sm: 0 },
                pt: returnContent({ xs: 1.5, sm: 4 }, { xs: 3.5, sm: 4 }),
            }}
        >
            <Box
                sx={{
                    borderRadius: { xs: 2, sm: 2.5 },
                    backgroundImage: returnContent(
                        {
                            xs: 'url(/home/images/home/hero/first-time-hero-bg-xs.jpeg)',
                            sm: 'url(/home/images/home/hero/first-time-hero-bg-sm.jpeg)',
                            md: 'url(/home/images/home/hero/first-time-hero-bg-sm.jpeg)',
                            lg: 'url(/home/images/home/hero/first-time-hero-bg-md.jpeg)',
                        },
                        {
                            xs: 'url(/home/images/home/hero/first-time-hero-bg-xs.webp)',
                            sm: 'url(/home/images/home/hero/first-time-hero-bg-sm.webp)',
                            md: 'url(/home/images/home/hero/first-time-hero-bg-md.webp)',
                            lg: 'url(/home/images/home/hero/first-time-hero-bg-lg.webp)',
                        },
                    ),
                    backgroundSize: 'cover',
                    backgroundPosition: returnContent(
                        {
                            xs: 'top 40% center',
                            sm: 'center',
                            md: 'top center',
                            lg: 'top center',
                        },
                        'center',
                    ),
                    height: returnContent(
                        {
                            xs: '320px',
                            sm: '416px',
                            md: '416px',
                        },
                        {
                            xs: '394px',
                            sm: '464px',
                            md: '560px',
                        },
                    ),
                }}
            >
                <Stack
                    spacing={{ xs: 1.5, md: 2 }}
                    sx={{
                        height: '100%',
                        pl: {
                            xs: 4,
                            sm: 5,
                            md: 6,
                        },
                        pr: {
                            xs: 1.5,
                            sm: 2,
                            md: 6,
                        },
                        py: {
                            xs: 3,
                            sm: 4,
                            md: 5,
                        },
                        p: returnContent(
                            {
                                xs: '1.5rem',
                            },
                            {},
                        ),
                    }}
                >
                    <Box>
                        <Typography
                            component="h2"
                            variant="h1"
                            sx={{
                                fontFamily: 'RBRationalNeueCondensedBold',
                                fontSize: {
                                    xs: '14.5vw',
                                    sm: '3.625rem',
                                    md: returnContent('4.0625rem', '5.25rem'),
                                    lg: '6.5rem',
                                },
                            }}
                        >
                            <TextReveal
                                text="WANNA DRIVE?"
                                sx={{
                                    height: {
                                        xs: 'auto',
                                        sm: '57px',
                                        md: returnContent('60px', '87px'),
                                        lg: '105px',
                                    },
                                }}
                            />
                        </Typography>
                        <Typography
                            component="h1"
                            sx={{
                                typography: { xs: 'subtitle1', sm: 'h5' },
                                maxWidth: returnContent(
                                    { xs: '100%', sm: '325px', md: '360px', lg: '528px' },
                                    { xs: '281px', md: '421px', lg: '528px' },
                                ),
                            }}
                        >
                            {returnContent(
                                'We make it easy to buy and sell your car',
                                'A car buying and selling experience designed for you',
                            )}
                        </Typography>
                    </Box>
                    <Button
                        size={returnContent(isMobile ? 'medium' : 'large', 'large')}
                        color="cta"
                        sx={{ width: returnContent({ xs: '150px', sm: '200px' }, { xs: '144px', sm: '200px' }) }}
                        onClick={handleOnClick}
                    >
                        {returnContent('Find your car', 'Search cars')}
                    </Button>
                </Stack>
            </Box>
        </KmxContainer>
    );
};
